import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Instagram, ArrowRight } from 'lucide-react';
import Logo from './Logo';

export const Footer = () => {
  return (
    <footer data-testid="main-footer" className="bg-[#0F2A4A] text-white/90 py-20">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Top Section - CTA */}
        <div className="border-b border-white/10 pb-16 mb-16">
          <div className="max-w-4xl">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ready to Make Your Brand Impossible to Ignore?
            </h2>
            <p className="text-lg text-white/70 mb-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Let's build marketing that actually moves your business forward.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                data-testid="footer-cta-primary"
                className="inline-flex items-center justify-center bg-[#3FC9E8] text-[#0F2A4A] font-medium px-8 py-4 hover:bg-[#1A9FE0] hover:text-white transition-all duration-300 active:scale-95"
              >
                Book a FREE Strategy Call <ArrowRight className="ml-2" size={20} />
              </Link>
              <Link
                to="/contact?scorecard=true"
                data-testid="footer-cta-secondary"
                className="inline-flex items-center justify-center border-2 border-white/30 text-white bg-transparent hover:bg-white hover:text-[#0F2A4A] font-medium px-8 py-4 transition-all duration-300 active:scale-95"
              >
                Get The Brand Clarity Scorecard
              </Link>
            </div>
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Logo & Tagline */}
          <div className="md:col-span-1">
            <div className="mb-4">
              <Logo variant="light" />
            </div>
            <p className="text-sm text-white/60" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Marketing built for B2B growth.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Navigation
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to="/" data-testid="footer-nav-home" className="text-white/70 hover:text-[#3FC9E8] transition-colors duration-300">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/services" data-testid="footer-nav-services" className="text-white/70 hover:text-[#3FC9E8] transition-colors duration-300">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/about" data-testid="footer-nav-about" className="text-white/70 hover:text-[#3FC9E8] transition-colors duration-300">
                  About
                </Link>
              </li>
              <li>
                <Link to="/contact" data-testid="footer-nav-contact" className="text-white/70 hover:text-[#3FC9E8] transition-colors duration-300">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Services
            </h3>
            <ul className="space-y-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <li>
                <Link to="/services/fractional-cmo" data-testid="footer-service-cmo" className="text-white/70 hover:text-[#3FC9E8] transition-colors duration-300">
                  Fractional CMO
                </Link>
              </li>
              <li>
                <Link to="/services/positioning-messaging" data-testid="footer-service-positioning" className="text-white/70 hover:text-[#3FC9E8] transition-colors duration-300">
                  Positioning & Messaging
                </Link>
              </li>
              <li>
                <Link to="/services/demand-generation" data-testid="footer-service-demand" className="text-white/70 hover:text-[#3FC9E8] transition-colors duration-300">
                  Demand Generation
                </Link>
              </li>
              <li>
                <Link to="/services/content-seo" data-testid="footer-service-content" className="text-white/70 hover:text-[#3FC9E8] transition-colors duration-300">
                  Content & SEO Strategy
                </Link>
              </li>
            </ul>
          </div>

          {/* Connect */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-4 uppercase tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Connect
            </h3>
            <div className="flex space-x-4 mb-6">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin"
                className="text-white/70 hover:text-[#3FC9E8] transition-colors duration-300"
              >
                <Linkedin size={20} />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="text-white/70 hover:text-[#3FC9E8] transition-colors duration-300"
              >
                <Instagram size={20} />
              </a>
            </div>
            <p className="text-sm text-white/60" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              www.theb2bdigital.com
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-white/60" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            © 2026 The B2B Digital. All rights reserved.
          </p>
          <div className="flex space-x-6 text-sm">
            <a href="#" className="text-white/60 hover:text-[#3FC9E8] transition-colors duration-300">
              Privacy Policy
            </a>
            <a href="#" className="text-white/60 hover:text-[#3FC9E8] transition-colors duration-300">
              Terms of Use
            </a>
            <a href="#" className="text-white/60 hover:text-[#3FC9E8] transition-colors duration-300">
              Cookie Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;